/**
 * Aminii Media Upload & Optimization Utility
 * Powered by Cloudflare R2 Object Storage
 *
 * Direct client-side browser upload with native WebCrypto SigV4 signing.
 * The backend server is never burdened with image/video payloads.
 */

import { isR2Configured, uploadToR2, compressImage, getCompressedFileInfo } from "./r2Upload";

const ALLOWED_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
  "image/avif",
  "image/gif",
];

const ALLOWED_VIDEO_TYPES = [
  "video/mp4",
  "video/quicktime",
  "video/webm",
  "video/x-m4v",
  "video/ogg",
];

const MAX_VIDEO_SIZE_MB = 100;
const MAX_VIDEO_SIZE_BYTES = MAX_VIDEO_SIZE_MB * 1024 * 1024;
const MAX_FILE_SIZE_MB = 15;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const MAX_FILES = 10;

/**
 * Validates a single File object before upload.
 */
export function validateImageFile(file) {
  if (!ALLOWED_TYPES.includes(file.type)) {
    return {
      valid: false,
      error: `"${file.name}" is not a supported image format. Use JPG, PNG, or WebP.`,
    };
  }
  if (file.size > MAX_FILE_SIZE_BYTES) {
    return {
      valid: false,
      error: `"${file.name}" exceeds the ${MAX_FILE_SIZE_MB}MB size limit.`,
    };
  }
  return { valid: true, error: null };
}

/**
 * Validates a single video File object before upload.
 */
export function validateVideoFile(file) {
  const isAllowedType =
    ALLOWED_VIDEO_TYPES.includes(file.type) ||
    /\.(mp4|mov|webm|m4v|ogg)$/i.test(file.name);

  if (!isAllowedType) {
    return {
      valid: false,
      error: `"${file.name}" is not a supported video format. Please upload MP4, MOV, or WebM.`,
    };
  }
  if (file.size > MAX_VIDEO_SIZE_BYTES) {
    return {
      valid: false,
      error: `"${file.name}" exceeds the ${MAX_VIDEO_SIZE_MB}MB size limit.`,
    };
  }
  return { valid: true, error: null };
}

/**
 * Uploads a single image File directly to Cloudflare R2
 * - Automatically compresses large images to high-quality WebP client-side
 *
 * @param {File} file - The image file to upload
 * @param {function(number): void} [onProgress] - Called with 0-100 progress
 * @returns {Promise<string>} - Resolves to the public URL of the uploaded image
 */
export async function uploadImage(file, onProgress) {
  if (!isR2Configured()) {
    throw new Error(
      "Cloudflare R2 is not configured. Please add REACT_APP_R2_ACCOUNT_ID, REACT_APP_R2_ACCESS_KEY_ID, and REACT_APP_R2_SECRET_ACCESS_KEY to your environment variables."
    );
  }

  if (typeof onProgress === "function") onProgress(15);
  // Compress image to 1200px retina, WebP where supported (~100-150KB)
  const compressedBlob = await compressImage(file, 1200, 0.85);
  if (typeof onProgress === "function") onProgress(50);

  const { contentType, extension } = getCompressedFileInfo(compressedBlob);
  const cleanName = (file.name || "image")
    .replace(/[^a-zA-Z0-9._-]/g, "_")
    .replace(/\.[^/.]+$/, "");
  const destinationKey = `products/${Date.now()}_${cleanName}.${extension}`;

  const publicUrl = await uploadToR2(compressedBlob, destinationKey, contentType);
  if (typeof onProgress === "function") onProgress(100);

  return publicUrl;
}

// Backwards-compatible alias for existing components (AdminAminii, AdminInventory)
export const uploadImageToCloudinary = uploadImage;

/**
 * Uploads multiple image files to Cloudflare R2 in parallel.
 *
 * @param {File[]} files - Array of image files (max 10)
 * @param {function(number, number): void} [onFileProgress]
 * @returns {Promise<string[]>} - Array of public URLs
 */
export async function uploadImages(files, onFileProgress) {
  if (!files || files.length === 0) return [];
  if (files.length > MAX_FILES) {
    throw new Error(`You can upload a maximum of ${MAX_FILES} images at once.`);
  }

  const uploads = files.map((file, index) =>
    uploadImage(file, (percent) => {
      if (typeof onFileProgress === "function") {
        onFileProgress(index, percent);
      }
    })
  );

  return Promise.all(uploads);
}

// Backwards-compatible alias
export const uploadImagesToCloudinary = uploadImages;

/**
 * Uploads a single video File directly to Cloudflare R2
 *
 * @param {File} file - The video file to upload (.mp4, .mov, .webm)
 * @param {function(number): void} [onProgress] - Called with 0-100 progress
 * @returns {Promise<string>} - Resolves to the public URL
 */
export async function uploadVideo(file, onProgress) {
  if (!isR2Configured()) {
    throw new Error(
      "Cloudflare R2 is not configured. Please add REACT_APP_R2_ACCOUNT_ID, REACT_APP_R2_ACCESS_KEY_ID, and REACT_APP_R2_SECRET_ACCESS_KEY to your environment variables."
    );
  }

  if (typeof onProgress === "function") onProgress(15);
  const cleanName = (file.name || "video.mp4").replace(/[^a-zA-Z0-9._-]/g, "_");
  const destinationKey = `reels/${Date.now()}_${cleanName}`;
  const contentType = file.type || "video/mp4";

  const publicUrl = await uploadToR2(file, destinationKey, contentType);
  if (typeof onProgress === "function") onProgress(100);

  return publicUrl;
}

// Backwards-compatible alias
export const uploadVideoToCloudinary = uploadVideo;

/**
 * URL Optimization Helpers:
 * Since Cloudflare R2 images are pre-compressed into WebP on upload,
 * URLs are served directly without expensive on-the-fly transformations.
 */
export function getOptimizedImageUrl(url) {
  return url || "";
}

export function getOptimizedVideoUrl(url) {
  return url || "";
}

export function getTransformedImageUrl(url) {
  return url || "";
}

export { MAX_FILES, MAX_FILE_SIZE_MB, ALLOWED_TYPES, ALLOWED_VIDEO_TYPES, MAX_VIDEO_SIZE_MB };
